import React, { useState, useEffect } from 'react';
import './BookingSection.css';
import servicesData from '../../data/services';

const formatPhoneNumber = (value) => {
  const cleaned = ('' + value).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]} - ${match[3]}` : value;
};

const capitalize = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

// Format a 'YYYY-MM-DD' string without shifting the day across timezones.
// `new Date('2026-06-25')` parses as midnight UTC and rolls back a day in US
// timezones, so build the date from its parts (local midnight) instead.
const formatDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const timeSlots = [
  '8:00 AM', '9:00 AM', '10:00 AM', '10:30 AM'
];

const filteredTimeSlots = timeSlots.filter(slot => {
  const [time, modifier] = slot.split(' ');
  let [hours] = time.split(':').map(Number);
  if (modifier === 'PM' && hours > 12) hours += 12;
  if (modifier === 'PM' && hours >= 12) return false;
  return true;
});

const BookingSection = () => {
  const [errorFields, setErrorFields] = useState([]);
  const now = new Date();
  const today = new Date();
  if (now.getHours() >= 16) {
    today.setDate(today.getDate() + 1);
  }

  const todayStr = today.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }); // 'YYYY-MM-DD' format in local timezone

  const initialDate = new Date(today);

  const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth());
  const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    date: todayStr,
    time: '',
    service: '',
    vehicleType: '',
    smsConsent: false, // <- Add this
  });
  const [bookedTimes, setBookedTimes] = useState([]);
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [rewards, setRewards] = useState(null);
  const [progress, setProgress] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [rewardChoice, setRewardChoice] = useState(''); // '', 'use', 'save'
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const useReward = async () => {
    setRewardChoice('use');
    if (bookingId) {
      try { await fetch(`https://jgdetailing.zyrixcode.com/api/bookings/${bookingId}/request-reward`, { method: 'POST' }); } catch (e) { /* ignore */ }
    }
  };

  // Reward-program terms (only shown when the program is on).
  useEffect(() => {
    fetch('https://jgdetailing.zyrixcode.com/api/rewards-info')
      .then((res) => res.json())
      .then((data) => setRewards(data))
      .catch(() => {});
  }, []);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDay = new Date(currentYear, currentMonth, 1).getDay(); // 0 = Sunday
  const offset = (firstDay + 6) % 7; // Align Monday as start

  useEffect(() => {
    if (form.date) {
      fetch(`https://jgdetailing.zyrixcode.com/api/bookings?date=${form.date}&location=shop1`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setBookedTimes(data.map(entry => entry.time));
            console.log('📅 Booked times fetched:', data);
          } else {
            if (data.error !== 'Not found') {
              console.error('⚠️ Unexpected data format:', data);
            }
            setBookedTimes([]);
          }
        })
        .catch(err => console.error('❌ Fetch error:', err));
    }
  }, [form.date]);

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        window.location.href = '/';
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'firstName' || name === 'lastName') {
      newValue = capitalize(value);
    } else if (name === 'phoneNumber') {
      newValue = formatPhoneNumber(value);
    }
    setForm({ ...form, [name]: newValue });
  };

  const handleSubmit = async () => {
    const requiredFields = ['firstName', 'lastName', 'email', 'phoneNumber', 'date', 'time', 'service', 'vehicleType', 'smsConsent'];
    const invalidFields = requiredFields.filter(field => !form[field]);
    setErrorFields(invalidFields);

    if (invalidFields.length > 0) {
      setError('Please fill in all fields.');
      return;
    }
    if (!isValidEmail(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate and clean phone number
    const cleanedPhone = form.phoneNumber.replace(/\D/g, '');
    if (cleanedPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    // Log form data before submission
    console.log(form);

    try {
      const res = await fetch('https://jgdetailing.zyrixcode.com/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, location: 'shop1', smsConsent: !!form.smsConsent, phoneNumber: cleanedPhone })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unknown error');
      setSubmitted(true);
      setBookingId(data.bookingId || null);
      // Load the customer's reward progress for the confirmation screen.
      fetch(`https://jgdetailing.zyrixcode.com/api/rewards-progress?phone=${cleanedPhone}`)
        .then((r) => r.json()).then((p) => setProgress(p)).catch(() => {});
      const bookedRes = await fetch(`https://jgdetailing.zyrixcode.com/api/bookings?date=${form.date}&location=shop1`);
      const bookedData = await bookedRes.json();
      if (Array.isArray(bookedData)) {
        setBookedTimes(bookedData.map(entry => entry.time));
      }
      await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          access_key: "4735d34a-0c64-44ce-b0d4-23d739f41765",
          subject: "New Appointment Booking",
          from_name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          message: `
        🚨 ${form.firstName} ${form.lastName} has just booked a service!
        
        📅 Date: ${form.date}
        🕒 Time: ${form.time}
        🚗 Vehicle Type: ${form.vehicleType}
        🧼 Service: ${form.service}
        📞 Phone: ${form.phoneNumber}
        
      `
        })
      });
    } catch (err) {
      setError(err.message);
      setForm({ ...form, time: '' }); // Clear selected time if submission failed
    }
  };

  const selectedService = servicesData.find(s => s.title === form.service);
  const price = form.vehicleType === 'SUV'
    ? selectedService?.priceSUV
    : form.vehicleType === 'Truck'
    ? selectedService?.priceTruck
    : form.vehicleType === 'VAN'
    ? selectedService?.priceVAN
    : selectedService?.priceCar;

  return (
    <>
    <div className="booking-banner">
    <h1 className="booking-heading">Book Your Appointment</h1>
  </div>
    <div className="booking-container">
      <div className="booking-steps">
        <div className={`bk-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}><span className="bk-step-num">1</span> Date &amp; Time</div>
        <div className="bk-step-bar"><i style={{ width: step > 1 ? '100%' : '0%' }} /></div>
        <div className={`bk-step ${step >= 2 ? 'active' : ''}`}><span className="bk-step-num">2</span> Your Details</div>
      </div>
      {step === 1 && (
        <div className="calendar-step">
          <div className="calendar-container">
            <div className="calendar-header-row">
              <button className="nav-arrow" onClick={handlePrevMonth}>← Prev Month</button>
              <h2>{new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
              <button className="nav-arrow" onClick={handleNextMonth}>Next Month →</button>
            </div>
            <div className="calendar-days-row">
              {daysOfWeek.map(day => <div key={day} className="calendar-day">{day}</div>)}
            </div>
            <div className="calendar-grid">
              {Array.from({ length: offset }).map((__, i) => (
                <div key={`empty-${i}`} className="calendar-cell empty"></div>
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const currentDate = new Date(currentYear, currentMonth, day);
                const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
                const isPastDate = currentDate.setHours(0,0,0,0) < today.setHours(0,0,0,0) || isWeekend;
                return (
                  <div
                    key={day}
                    className={`calendar-cell ${form.date === dateStr ? 'selected' : ''} ${isPastDate ? 'disabled-date' : ''}`}
                    onClick={() => {
                      if (!isPastDate) {
                        setForm({ ...form, date: dateStr });
                      }
                    }}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </div>
          <h3>Available Times</h3>
          <div className="time-grid">
            {filteredTimeSlots.map((slot) => {
              const todayStrLocal = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' }); // 'YYYY-MM-DD' format
              const isToday = form.date === todayStrLocal;
              let isPastTime = false;

              if (isToday) {
                const [time, modifier] = slot.split(' ');
                let [hours, minutes] = time.split(':').map(Number);
                if (modifier === 'PM' && hours !== 12) hours += 12;
                if (modifier === 'AM' && hours === 12) hours = 0;

                const slotDateTime = new Date();
                slotDateTime.setHours(hours, minutes, 0, 0);

                let isAfterNoon = false;
                if (modifier === 'PM' && hours > 12) isAfterNoon = true;
                if (modifier === 'PM' && hours >= 12) isAfterNoon = true;

                isPastTime = slotDateTime < new Date() || isAfterNoon;
              }
              // Normalized slot and bookedTimes for comparison
              const normalizedSlot = slot.trim().toLowerCase();
              const isBooked = bookedTimes.map(t => t.trim().toLowerCase()).includes(normalizedSlot);
              return (
                <button
                  key={slot}
                  className={`time-btn ${form.time === slot ? 'selected' : ''}`}
                  disabled={isBooked || isPastTime}
                  onClick={() => {
                    if (!(isBooked || isPastTime)) {
                      setForm({ ...form, time: slot });
                    }
                  }}
                >
                  {slot} {isBooked ? '(Unavailable)' : ''}
                </button>
              );
            })}
          </div>
          {form.time && (
            <button className="next-btn" onClick={() => {
              setStep(2);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              Continue
            </button>
          )}
        </div>
      )}

      {step === 2 && (
        <>
        <button className="back-btn" onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>← Back to date &amp; time</button>
        <div className="details-step-wrapper">
          <div className="client-info">
            <h2>Your Contact Info</h2>
            <label htmlFor="firstName">First Name</label>
            <input name="firstName" id="firstName" className={errorFields.includes('firstName') ? 'has-error' : ''} placeholder="First Name" value={form.firstName} onChange={handleChange} />

            <label htmlFor="lastName">Last Name</label>
            <input name="lastName" id="lastName" className={errorFields.includes('lastName') ? 'has-error' : ''} placeholder="Last Name" value={form.lastName} onChange={handleChange} />

            <label htmlFor="email">Email</label>
            <input name="email" id="email" className={errorFields.includes('email') ? 'has-error' : ''} placeholder="Email" value={form.email} onChange={handleChange} />

            <label htmlFor="phoneNumber">Phone Number</label>
            <input name="phoneNumber" id="phoneNumber" className={errorFields.includes('phoneNumber') ? 'has-error' : ''} placeholder="Phone Number" value={form.phoneNumber} onChange={handleChange} />
            
          </div>

          <div className="service-details">
            <h2>Select Service</h2>

            <label htmlFor="service">Service</label>
            <select name="service" id="service" className={errorFields.includes('service') ? 'has-error' : ''} value={form.service} onChange={handleChange}>
              <option value="">Select Service</option>
              {servicesData.map((service, i) => (
                <option key={i} value={service.title}>{service.title}</option>
              ))}
            </select>

           <label htmlFor="vehicleType">Vehicle Type</label>
            <select name="vehicleType" id="vehicleType" className={errorFields.includes('vehicleType') ? 'has-error' : ''} value={form.vehicleType} onChange={handleChange}>
              <option value="">Select Vehicle Type</option>
              <option value="Sedan">Sedan</option>
              <option value="SUV">SUV</option>
              <option value="Truck">Truck</option>
              <option value="VAN">VAN</option>
            </select>

            {selectedService && (
              <div className="summary-box">
                <h4>Summary</h4>
                <p>{formatDateLabel(form.date)} at {form.time}</p>
                <p><strong>{selectedService.title}</strong></p>
                <p>{selectedService.description}</p>
                <p>Vehicle: {form.vehicleType}</p>
                <p>Total: <strong>{price}</strong></p>
              </div>
            )}

                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="smsConsent"
                    checked={form.smsConsent || false}
                    onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })}
                  />
                  <label htmlFor="smsConsent">
                    I agree to receive text messages regarding my booking.
                  </label>
                </div>

                {rewards && rewards.enabled && (
                  <div className="reward-note">
                    <span className="gift" role="img" aria-label="gift">🎁</span>
                    <span>
                      <strong>Earn a {rewards.reward_label || 'Free Wash'}!</strong> Get {rewards.punch_goal || 3} details
                      done with us and your next one is on the house — earned automatically with the phone number you book under.
                    </span>
                  </div>
                )}

            {submitted ? (
              <div className="success-box">
                <p className="success-msg">
                  ✅ Booking confirmed! We will contact you shortly to confirm your appointment.
                </p>

                {progress && progress.enabled && (
                  progress.free_available > 0 ? (
                    <div className="reward-card earned">
                      <div className="reward-stars">
                        {Array.from({ length: progress.punch_goal || 3 }).map((_, i) => (
                          <span key={i} className="rw-star on">★</span>
                        ))}
                      </div>
                      <p className="reward-title">🎉 You have a {progress.reward_label || 'Free Wash'}!</p>
                      {rewardChoice === 'use' ? (
                        <p className="reward-done">Awesome — we'll apply your {progress.reward_label || 'Free Wash'} at this appointment.</p>
                      ) : rewardChoice === 'save' ? (
                        <p className="reward-done">No problem — it'll be waiting for you next time.</p>
                      ) : (
                        <>
                          <p className="reward-ask">Want to use it on this visit?</p>
                          <div className="reward-actions">
                            <button type="button" className="reward-yes" onClick={useReward}>Yes, use it now</button>
                            <button type="button" className="reward-no" onClick={() => setRewardChoice('save')}>Save for later</button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="reward-card">
                      <div className="reward-stars">
                        {Array.from({ length: progress.punch_goal || 3 }).map((_, i) => (
                          <span key={i} className={`rw-star ${i < progress.punches ? 'on' : ''}`}>★</span>
                        ))}
                      </div>
                      <p className="reward-title">
                        {progress.punches} of {progress.punch_goal} details
                      </p>
                      <p className="reward-ask">
                        {Math.max(0, (progress.punch_goal || 3) - (progress.punches || 0))} more until your {progress.reward_label || 'Free Wash'} is on us!
                      </p>
                    </div>
                  )
                )}
              </div>
            ) : (
              <button onClick={handleSubmit} className="confirm-btn">Confirm Booking</button>
            )}

            {error && <p className="error-msg">❌ {error}</p>}
          </div>
        </div>
        </>
      )}
    </div>
    </>
  );
};

export default BookingSection;